// Brokerage / Sub-Broker PR-6 — the external brokerage-firm portal data reads.
// Firm-scoped, read-only projections; the server resolves the firm from the
// session (LinkedBrokerageId) and re-scopes each read across the firm's active
// roster. All calls hit /api/portal/brokerage/* through the portal client.

import { portalApi } from "./client";
import type { BrokerFinanceSummary } from "./brokerFinance";
import type { BrokerDashboardChipGroup, PortalDealSubmissionListDto } from "./types";

export interface BrokeragePipelineMix {
  stage: string;
  count: number;
}

export interface BrokerageProductionSubBroker {
  subBrokerId: string;
  subBrokerName: string;
  dealCount: number;
  ledgerGross: number;
  // A ledger row was booked before a split was configured (gross-only surface;
  // net/cut are no longer shown).
  anyUnallocated: boolean;
}

export interface BrokerageProduction {
  brokerageId: string;
  subBrokerCount: number;
  dealsSourcedCount: number;
  dealsSourcedVolume: number;
  loansOriginatedCount: number;
  loansOriginatedTotal: number;
  activeDealCount: number;
  closedDealCount: number;
  pipelineMix: BrokeragePipelineMix[];
  totalGross: number;
  subBrokers: BrokerageProductionSubBroker[];
}

export interface BrokeragePayments {
  totalGross: number;
  subBrokers: BrokerageProductionSubBroker[];
  commissions: BrokerFinanceSummary;
}

export interface BrokerageRosterRow {
  subBrokerId: string;
  subBrokerName: string;
  status: string; // Active | Proposed | Declined | Ended
  isPrimary: boolean;
  dealCount: number;
  ledgerGross: number;
  anyUnallocated: boolean;
  // Gross-only visibility of this sub-broker's OWN finance view: Full (show
  // gross) | NetOnly (legacy, treated as show) | None (hidden). Only juniors
  // are gated on read.
  financialVisibility: string;
  // Lead Conversion Router (WS-3) — the sub-broker's immutable payee type
  // ("Independent" | "Junior").
  brokerType: string;
}

// The firm-scoped DETAIL of ONE sub-broker (opened from a Roster row). Composes
// the firm's newest affiliation (identity / status / dates / gross-only
// visibility) with the shared gross-only production rollup. GROSS-ONLY — no
// net/cut/owed. The sub-broker's own deals are read separately via dealsList.
export interface BrokerageSubBrokerDetail {
  subBrokerId: string;
  subBrokerName: string;
  brokerType: string; // Independent | Junior
  status: string; // Active | Proposed | Declined | Ended
  isPrimary: boolean;
  // Full (show gross) | NetOnly (legacy, treated as show) | None (hidden).
  financialVisibility: string;
  joinedAt: string;
  acceptedAt: string | null;
  dealCount: number;
  ledgerGross: number;
  anyUnallocated: boolean;
}

// PR-10 — one portal-safe activity feed row (application + deal-submission
// lifecycle, attributed to the sub-broker; no money events).
export interface BrokerageActivityItem {
  id: string;
  subBrokerId: string;
  subBrokerName: string;
  title: string;
  occurredAt: string;
  category: string; // "application" | "deal"
}

// PR-7 — result of inviting a sub-broker onto the firm's roster.
export interface BrokerageInviteResult {
  affiliationId: string;
  brokerId: string;
  email: string;
  brokerCreated: boolean;
  /** The broker already had a live (Proposed/Active) affiliation with this firm —
   *  the invite re-notified a pending proposal or was a no-op for an active one. */
  alreadyAffiliated: boolean;
  status: string;
}

export interface InviteSubBrokerBody {
  name: string;
  email: string;
  company?: string;
  /** PR-8 — invite as a Junior (paid through this firm) rather than Independent
   *  (paid directly). Applied only to a brand-new broker; the server rejects a
   *  type change on an existing broker. Omit/false → Independent. */
  asJunior?: boolean;
}

export interface BrokeragePortalDeal {
  id: string;
  dealNumber: string;
  status: string;
  businessName: string | null;
  purchasePrice: number;
  sellerNoteAmount: number;
  stageEnteredDate: string;
  createdAt: string;
}

export interface PagedResult<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface BrokerageDealsQuery {
  group?: BrokerDashboardChipGroup;
  q?: string;
  page?: number;
  pageSize?: number;
  /** Drill into a single sub-broker's deals; server-checked against the roster. */
  subBrokerId?: string;
}

export const brokeragePortalApi = {
  /** Home — firm production + split-ledger rollup (roster matrix + totals). */
  production: () => portalApi.get<BrokerageProduction>("/brokerage/production"),

  /** Deals — the roster's deal-submission status dashboard. */
  deals: (params: BrokerageDealsQuery = {}) =>
    portalApi.get<PortalDealSubmissionListDto>("/brokerage/deals", {
      group: params.group,
      q: params.q,
      page: params.page,
      pageSize: params.pageSize,
      subBrokerId: params.subBrokerId,
    }),

  /** Deals — a compact, paginated deal rollup across the roster (or one sub-broker). */
  dealsList: (params: { page?: number; pageSize?: number; subBrokerId?: string } = {}) =>
    portalApi.get<PagedResult<BrokeragePortalDeal>>("/brokerage/deals/list", {
      page: params.page,
      pageSize: params.pageSize,
      subBrokerId: params.subBrokerId,
    }),

  /** Payments — the firm's full financials: split totals + the roster commission carve. */
  payments: () => portalApi.get<BrokeragePayments>("/brokerage/payments"),

  /** On-demand firm commission-statement PDF (period = YYYY-MM). */
  statement: (period: string) => portalApi.getBlob("/brokerage/payments/statement", { period }),

  /** Roster — the firm's affiliated sub-brokers across the full lifecycle
   *  (Active / Proposed / Declined / Ended). */
  roster: () => portalApi.get<BrokerageRosterRow[]>("/brokerage/roster"),

  /** The firm-scoped detail of ONE sub-broker (opened from a Roster row).
   *  Fail-closed — an id the firm has no affiliation with 404s. Deals for the
   *  sub-broker are read separately via `dealsList({ subBrokerId })`. */
  subBroker: (subBrokerId: string) =>
    portalApi.get<BrokerageSubBrokerDetail>(`/brokerage/roster/${subBrokerId}`),

  /** Activity — the roster's application + deal-submission lifecycle feed,
   *  portal-safe (sub-broker attribution, no money events). */
  activity: () => portalApi.get<BrokerageActivityItem[]>("/brokerage/activity"),

  /** PR-7 — invite a sub-broker onto the firm's roster. Creates a Proposed
   *  affiliation the broker accepts from their own portal (two-sided consent). */
  inviteSubBroker: (body: InviteSubBrokerBody) =>
    portalApi.post<BrokerageInviteResult>("/brokerage/invite", body),

  /** Gross-only visibility — Show gross (true) / Hide (false) a sub-broker's own
   *  finance view. Applies to the firm's active affiliation; only juniors are
   *  gated on read. */
  setVisibility: (subBrokerId: string, showGross: boolean) =>
    portalApi.post<{ partnerId: string; visibility: string }>(
      `/brokerage/roster/${subBrokerId}/visibility`,
      { showGross },
    ),
};
